/**
 * SimulationScene - Life Function Simulator gameplay
 * Players observe and interact with cell life functions
 */

import Phaser from 'phaser';
import { LifeFunctionSystem, LifeFunction, StimulusType } from '@/game/systems/LifeFunctionSystem';
import { CellEffectsSystem } from '@/procedural/graphics/CellEffectsSystem';
import { LifeFunctionSounds } from '@/audio/LifeFunctionSounds';
import { CellRenderer } from '@/procedural/graphics';
import { getOrganismTemplate } from '@/data';
import { getLevel, getChapterForLevel } from '@/data';
import type { Level } from '@/types';

export interface SimulationSceneConfig {
  levelId: string;
}

interface FunctionButton {
  container: Phaser.GameObjects.Container;
  function: LifeFunction;
  enabled: boolean;
}

/**
 * SimulationScene handles life function observation and interaction
 */
export class SimulationScene extends Phaser.Scene {
  private levelId!: string;
  private level!: Level;

  /** Systems */
  private lifeFunctionSystem!: LifeFunctionSystem;
  private effectsSystem!: CellEffectsSystem;
  private sounds!: LifeFunctionSounds;

  /** Cell display */
  private cellContainer!: Phaser.GameObjects.Container;
  private cellId = 'simulation_cell';

  /** UI elements */
  private functionButtons: FunctionButton[] = [];
  private statePanel!: Phaser.GameObjects.Container;
  private stateTexts: Map<string, Phaser.GameObjects.Text> = new Map();
  private observationLog!: Phaser.GameObjects.Container;
  private logEntries: Phaser.GameObjects.Text[] = [];

  /** Colors */
  private static readonly COLORS = {
    panelBg: 0x0a1628,
    panelBorder: 0x1e3a5f,
    buttonActive: 0x2b6cb0,
    buttonHover: 0x3182ce,
    buttonDisabled: 0x2d3748,
    textPrimary: '#e2e8f0',
    textSecondary: '#90cdf4',
    accent: 0x4299e1,
  };

  constructor() {
    super({ key: 'SimulationScene' });
  }

  init(data: SimulationSceneConfig): void {
    this.levelId = data.levelId;
  }

  create(): void {
    // Load level data
    const level = getLevel(this.levelId);
    if (!level) {
      console.error(`Level not found: ${this.levelId}`);
      this.scene.start('MenuScene');
      return;
    }
    this.level = level;

    // Create background
    this.createBackground();

    // Initialize systems
    this.initializeSystems();

    // Create the main cell
    this.createCell();

    // Create UI
    this.createUI();

    // Setup event listeners
    this.setupEventListeners();

    // Start simulation
    this.lifeFunctionSystem.start();

    // Initialize audio
    this.sounds.initialize();
  }

  override update(_time: number, delta: number): void {
    this.lifeFunctionSystem.update(delta);
    this.updateStateDisplay();
  }

  /**
   * Create background
   */
  private createBackground(): void {
    const { width, height } = this.cameras.main;

    // Dark blue gradient
    const bg = this.add.graphics();
    bg.fillStyle(0x0a1628, 1);
    bg.fillRect(0, 0, width, height);

    // Add subtle gradient
    for (let i = 0; i < 20; i++) {
      const alpha = 0.01 + (i / 20) * 0.02;
      bg.fillStyle(0x1a365d, alpha);
      bg.fillRect(0, (height / 20) * i, width, height / 20);
    }
  }

  /**
   * Initialize systems
   */
  private initializeSystems(): void {
    this.lifeFunctionSystem = new LifeFunctionSystem(this, {
      metabolismRate: 1.5,
      growthRate: 0.015,
      divisionDuration: 6000,
    });

    this.effectsSystem = new CellEffectsSystem(this, {
      intensity: 1.2,
    });

    this.sounds = new LifeFunctionSounds({
      masterVolume: 0.5,
    });
  }

  /**
   * Create the central cell display
   */
  private createCell(): void {
    const { width, height } = this.cameras.main;

    // Get a default organism template (amoeba for simulation)
    const template = getOrganismTemplate('amoeba');
    if (!template) {
      console.error('Amoeba template not found');
      return;
    }

    // Render the cell
    const renderer = new CellRenderer(this);
    const cellResult = renderer.renderCell(template.cellConfig, Date.now());

    this.cellContainer = cellResult.container;
    this.cellContainer.setPosition(width / 2, height / 2 - 50);
    this.cellContainer.setScale(1.5);

    // Register with life function system
    this.lifeFunctionSystem.registerCell(this.cellId, {
      energy: 70,
      size: 1.0,
      wasteLevel: 15,
      oxygenLevel: 85,
      co2Level: 15,
      temperature: 0,
    });
  }

  /**
   * Create UI elements
   */
  private createUI(): void {
    this.createTitle();
    this.createFunctionButtons();
    this.createStatePanel();
    this.createObservationLog();
    this.createBackButton();
    this.createStimulusButtons();
  }

  /**
   * Create title
   */
  private createTitle(): void {
    const { width } = this.cameras.main;
    const chapter = getChapterForLevel(this.levelId);

    this.add.text(width / 2, 30, 'Life Function Simulator', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '24px',
      color: '#4299e1',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    this.add.text(width / 2, 55, `${chapter?.title ?? ''} - ${this.level.name}`, {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '14px',
      color: '#90cdf4',
    }).setOrigin(0.5);
  }

  /**
   * Create function trigger buttons
   */
  private createFunctionButtons(): void {
    const startY = 150;
    const buttonSpacing = 55;

    const functions: { func: LifeFunction; label: string; icon: string }[] = [
      { func: LifeFunction.METABOLISM, label: 'Metabolism', icon: '⚡' },
      { func: LifeFunction.GROWTH, label: 'Growth', icon: '📈' },
      { func: LifeFunction.REPRODUCTION, label: 'Reproduce', icon: '🔄' },
      { func: LifeFunction.WASTE_REMOVAL, label: 'Excretion', icon: '💨' },
      { func: LifeFunction.GAS_EXCHANGE, label: 'Respiration', icon: '🌬️' },
      { func: LifeFunction.HOMEOSTASIS, label: 'Homeostasis', icon: '⚖️' },
    ];

    functions.forEach((item, index) => {
      const y = startY + index * buttonSpacing;
      const button = this.createFunctionButton(80, y, item.label, item.icon, item.func);
      this.functionButtons.push(button);
    });
  }

  /**
   * Create a single function button
   */
  private createFunctionButton(
    x: number,
    y: number,
    label: string,
    icon: string,
    func: LifeFunction
  ): FunctionButton {
    const container = this.add.container(x, y);
    const buttonWidth = 120;
    const buttonHeight = 45;

    // Background
    const bg = this.add.graphics();
    bg.fillStyle(SimulationScene.COLORS.buttonActive, 0.9);
    bg.fillRoundedRect(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight, 8);
    bg.lineStyle(1, SimulationScene.COLORS.accent, 0.5);
    bg.strokeRoundedRect(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight, 8);

    // Icon and label
    const text = this.add.text(0, 0, `${icon} ${label}`, {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '12px',
      color: SimulationScene.COLORS.textPrimary,
    }).setOrigin(0.5);

    container.add([bg, text]);
    container.setSize(buttonWidth, buttonHeight);
    container.setInteractive({ useHandCursor: true });

    // Hover effects
    container.on('pointerover', () => {
      bg.clear();
      bg.fillStyle(SimulationScene.COLORS.buttonHover, 1);
      bg.fillRoundedRect(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight, 8);
      bg.lineStyle(2, 0x68d391, 0.8);
      bg.strokeRoundedRect(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight, 8);
    });

    container.on('pointerout', () => {
      bg.clear();
      bg.fillStyle(SimulationScene.COLORS.buttonActive, 0.9);
      bg.fillRoundedRect(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight, 8);
      bg.lineStyle(1, SimulationScene.COLORS.accent, 0.5);
      bg.strokeRoundedRect(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight, 8);
    });

    container.on('pointerdown', () => {
      this.triggerLifeFunction(func);
    });

    return { container, function: func, enabled: true };
  }

  /**
   * Create stimulus buttons
   */
  private createStimulusButtons(): void {
    const { width } = this.cameras.main;
    const startX = width - 100;
    const startY = 150;

    const stimuli: { stimulus: StimulusType; label: string; icon: string }[] = [
      { stimulus: StimulusType.LIGHT, label: 'Light', icon: '☀️' },
      { stimulus: StimulusType.FOOD, label: 'Food', icon: '🍎' },
      { stimulus: StimulusType.TEMPERATURE, label: 'Heat', icon: '🌡️' },
      { stimulus: StimulusType.TOUCH, label: 'Touch', icon: '👆' },
    ];

    // Title
    this.add.text(startX, startY - 30, 'STIMULI', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '12px',
      color: '#90cdf4',
    }).setOrigin(0.5);

    stimuli.forEach((item, index) => {
      const y = startY + index * 50;
      this.createStimulusButton(startX, y, item.label, item.icon, item.stimulus);
    });
  }

  /**
   * Create a stimulus button
   */
  private createStimulusButton(
    x: number,
    y: number,
    _label: string,
    icon: string,
    stimulus: StimulusType
  ): void {
    const container = this.add.container(x, y);
    const size = 40;

    const bg = this.add.graphics();
    bg.fillStyle(0x2d4a6f, 0.8);
    bg.fillCircle(0, 0, size / 2);
    bg.lineStyle(1, SimulationScene.COLORS.accent, 0.5);
    bg.strokeCircle(0, 0, size / 2);

    const text = this.add.text(0, 0, icon, {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '18px',
    }).setOrigin(0.5);

    container.add([bg, text]);
    container.setSize(size, size);
    container.setInteractive({ useHandCursor: true });

    container.on('pointerdown', () => {
      this.applyStimulus(stimulus);
    });

    container.on('pointerover', () => {
      bg.clear();
      bg.fillStyle(0x3182ce, 1);
      bg.fillCircle(0, 0, size / 2);
      bg.lineStyle(2, 0x68d391, 0.8);
      bg.strokeCircle(0, 0, size / 2);
    });

    container.on('pointerout', () => {
      bg.clear();
      bg.fillStyle(0x2d4a6f, 0.8);
      bg.fillCircle(0, 0, size / 2);
      bg.lineStyle(1, SimulationScene.COLORS.accent, 0.5);
      bg.strokeCircle(0, 0, size / 2);
    });
  }

  /**
   * Create state panel showing cell vitals
   */
  private createStatePanel(): void {
    const { width, height } = this.cameras.main;

    this.statePanel = this.add.container(width / 2, height - 100);

    // Panel background
    const panelWidth = 400;
    const panelHeight = 80;
    const bg = this.add.graphics();
    bg.fillStyle(SimulationScene.COLORS.panelBg, 0.9);
    bg.fillRoundedRect(-panelWidth / 2, -panelHeight / 2, panelWidth, panelHeight, 10);
    bg.lineStyle(1, SimulationScene.COLORS.panelBorder, 0.7);
    bg.strokeRoundedRect(-panelWidth / 2, -panelHeight / 2, panelWidth, panelHeight, 10);

    this.statePanel.add(bg);

    // State labels
    const states = [
      { key: 'energy', label: 'Energy', x: -150 },
      { key: 'size', label: 'Size', x: -50 },
      { key: 'oxygen', label: 'O₂', x: 50 },
      { key: 'waste', label: 'Waste', x: 150 },
    ];

    states.forEach(state => {
      const label = this.add.text(state.x, -20, state.label, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '11px',
        color: '#90cdf4',
      }).setOrigin(0.5);

      const value = this.add.text(state.x, 5, '--', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '18px',
        color: '#e2e8f0',
        fontStyle: 'bold',
      }).setOrigin(0.5);

      this.stateTexts.set(state.key, value);
      this.statePanel.add([label, value]);
    });
  }

  /**
   * Create observation log
   */
  private createObservationLog(): void {
    const { height } = this.cameras.main;

    this.observationLog = this.add.container(80, height - 200);

    // Title
    const title = this.add.text(0, 0, 'OBSERVATION LOG', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '11px',
      color: '#90cdf4',
    });

    this.observationLog.add(title);
  }

  /**
   * Create back button
   */
  private createBackButton(): void {
    const container = this.add.container(60, 30);

    const bg = this.add.graphics();
    bg.fillStyle(0x1a365d, 0.8);
    bg.fillRoundedRect(-40, -15, 80, 30, 6);
    bg.lineStyle(1, SimulationScene.COLORS.accent, 0.5);
    bg.strokeRoundedRect(-40, -15, 80, 30, 6);

    const text = this.add.text(0, 0, '← Back', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '14px',
      color: '#e2e8f0',
    }).setOrigin(0.5);

    container.add([bg, text]);
    container.setSize(80, 30);
    container.setInteractive({ useHandCursor: true });

    container.on('pointerdown', () => this.exitScene());
    container.on('pointerover', () => {
      bg.clear();
      bg.fillStyle(0x2d4a6f, 1);
      bg.fillRoundedRect(-40, -15, 80, 30, 6);
      bg.lineStyle(1, 0x68d391, 0.8);
      bg.strokeRoundedRect(-40, -15, 80, 30, 6);
    });
    container.on('pointerout', () => {
      bg.clear();
      bg.fillStyle(0x1a365d, 0.8);
      bg.fillRoundedRect(-40, -15, 80, 30, 6);
      bg.lineStyle(1, SimulationScene.COLORS.accent, 0.5);
      bg.strokeRoundedRect(-40, -15, 80, 30, 6);
    });
  }

  /**
   * Setup event listeners
   */
  private setupEventListeners(): void {
    // Listen for life function events
    this.events.on('lifeFunction:event', this.handleLifeFunctionEvent, this);
    this.events.on('cellDivision:phase', this.handleDivisionPhase, this);

    // Keyboard shortcuts
    this.input.keyboard?.on('keydown-ESC', () => this.exitScene());
  }

  /**
   * Trigger a life function
   */
  private triggerLifeFunction(func: LifeFunction): void {
    const state = this.lifeFunctionSystem.getCellState(this.cellId);
    if (!state) return;

    // Check if function can be triggered
    if (func === LifeFunction.REPRODUCTION && !state.canReproduce) {
      this.addLogEntry('Cell not ready to reproduce (need energy ≥80, size ≥1.5)');
      return;
    }

    const success = this.lifeFunctionSystem.triggerFunction(this.cellId, func);

    if (success) {
      // Play sound
      this.sounds.playLifeFunctionSound(func);

      // Show visual effect
      this.showLifeFunctionEffect(func);

      // Add to log
      const name = LifeFunctionSystem.getFunctionName(func);
      this.addLogEntry(`Triggered: ${name}`);
    }
  }

  /**
   * Apply a stimulus
   */
  private applyStimulus(stimulus: StimulusType): void {
    this.lifeFunctionSystem.applyStimulus(this.cellId, stimulus);
    this.sounds.playLifeFunctionSound(LifeFunction.RESPONSE_TO_STIMULI, { stimulus });

    // Show response effect
    this.effectsSystem.visualizeResponse(this.cellContainer, this.cellId, stimulus, 1);

    this.addLogEntry(`Applied stimulus: ${stimulus}`);
  }

  /**
   * Show visual effect for a life function
   */
  private showLifeFunctionEffect(func: LifeFunction): void {
    switch (func) {
      case LifeFunction.METABOLISM:
        this.effectsSystem.visualizeMetabolism(this.cellContainer, this.cellId);
        break;
      case LifeFunction.REPRODUCTION:
        this.effectsSystem.visualizeCellDivision(this.cellContainer, this.cellId);
        break;
      case LifeFunction.GROWTH:
        const state = this.lifeFunctionSystem.getCellState(this.cellId);
        if (state) {
          this.effectsSystem.visualizeGrowth(this.cellContainer, this.cellId, state.size - 0.1, state.size);
        }
        break;
      case LifeFunction.WASTE_REMOVAL:
        this.effectsSystem.visualizeWasteRemoval(this.cellContainer, this.cellId, 20);
        break;
      case LifeFunction.GAS_EXCHANGE:
        this.effectsSystem.visualizeGasExchange(this.cellContainer, this.cellId);
        break;
      case LifeFunction.HOMEOSTASIS:
        const cellState = this.lifeFunctionSystem.getCellState(this.cellId);
        if (cellState) {
          this.effectsSystem.visualizeHomeostasis(this.cellContainer, this.cellId, 'temperature', cellState.temperature);
        }
        break;
    }
  }

  /**
   * Handle life function events
   */
  private handleLifeFunctionEvent(event: { function: LifeFunction; cellId: string; data: Record<string, unknown> }): void {
    if (event.cellId !== this.cellId) return;

    // Update UI based on event
    if (event.data.type === 'feed') {
      this.addLogEntry(`Fed cell: +${event.data.amount} energy`);
    }
  }

  /**
   * Handle division phase events
   */
  private handleDivisionPhase(event: { cellId: string; phase: string }): void {
    if (event.cellId !== this.cellId) return;

    const phaseNames: Record<string, string> = {
      interphase: 'DNA Replication',
      prophase: 'Chromosomes Condensing',
      metaphase: 'Chromosomes Aligning',
      anaphase: 'Chromosomes Separating',
      telophase: 'Nuclear Reform',
      cytokinesis: 'Cell Dividing',
      complete: 'Division Complete!',
    };

    this.addLogEntry(`Division: ${phaseNames[event.phase] ?? event.phase}`);
  }

  /**
   * Update state display
   */
  private updateStateDisplay(): void {
    const state = this.lifeFunctionSystem.getCellState(this.cellId);
    if (!state) return;

    const energyText = this.stateTexts.get('energy');
    if (energyText) {
      energyText.setText(`${Math.round(state.energy)}%`);
      energyText.setColor(state.energy < 30 ? '#fc8181' : state.energy > 70 ? '#68d391' : '#e2e8f0');
    }

    const sizeText = this.stateTexts.get('size');
    if (sizeText) {
      sizeText.setText(`${state.size.toFixed(2)}x`);
    }

    const oxygenText = this.stateTexts.get('oxygen');
    if (oxygenText) {
      oxygenText.setText(`${Math.round(state.oxygenLevel)}%`);
      oxygenText.setColor(state.oxygenLevel < 50 ? '#fc8181' : '#68d391');
    }

    const wasteText = this.stateTexts.get('waste');
    if (wasteText) {
      wasteText.setText(`${Math.round(state.wasteLevel)}%`);
      wasteText.setColor(state.wasteLevel > 70 ? '#fc8181' : '#68d391');
    }

    // Update cell scale based on size
    this.cellContainer.setScale(1.5 * state.size);
  }

  /**
   * Add entry to observation log
   */
  private addLogEntry(text: string): void {
    const y = 20 + this.logEntries.length * 18;

    // Limit entries
    if (this.logEntries.length >= 8) {
      const oldest = this.logEntries.shift();
      oldest?.destroy();

      // Move remaining entries up
      this.logEntries.forEach((entry, i) => {
        entry.setY(20 + i * 18);
      });
    }

    const entry = this.add.text(0, y, `• ${text}`, {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '10px',
      color: '#a0aec0',
    });

    this.observationLog.add(entry);
    this.logEntries.push(entry);

    // Fade in
    entry.setAlpha(0);
    this.tweens.add({
      targets: entry,
      alpha: 1,
      duration: 200,
    });
  }

  /**
   * Exit scene
   */
  private exitScene(): void {
    this.cleanup();
    this.scene.start('MenuScene');
  }

  /**
   * Clean up resources
   */
  private cleanup(): void {
    this.lifeFunctionSystem.destroy();
    this.effectsSystem.destroy();
    this.sounds.destroy();

    this.events.off('lifeFunction:event', this.handleLifeFunctionEvent, this);
    this.events.off('cellDivision:phase', this.handleDivisionPhase, this);
  }
}
